import { useCallback, useState } from "react";
import { dismissFinding, reopenFinding, type LedgerFinding } from "../../api/codeReview";

/** The reasons a dismissal can record. The first is the default. */
export const DISMISS_REASONS: ReadonlyArray<{ reason: string; hint: string }> = [
  { reason: "Intentional", hint: "The code is meant to be this way" },
  { reason: "Won't fix", hint: "Real, but not worth changing" },
];

const DISMISS_NOTE = "Dismissing is permanent. The reason is recorded against the finding, and Bob will not raise this defect again on this branch unless somebody reopens it.";

function actionNote(status: string): string {
  if (status === "dismissed") return "Reopening undoes the dismissal, so Bob raises this defect again on his next pass instead of absorbing it.";
  if (status === "fixed") return "Reopening puts a finding Bob recorded as gone back on the open list, for when the defect is not actually gone.";
  return DISMISS_NOTE;
}

/** Dismiss with a reason, and Reopen: admin only. Both throw on a refusal,
 *  and Bob's message is shown verbatim. In the `bar` layout the buttons are all
 *  that sit in the bar; the reason and the note open in a sheet above it. */
export default function FindingActions(
  { project, finding, onChanged, layout = "inline" }: {
    project: string;
    finding: LedgerFinding;
    onChanged: () => Promise<void>;
    layout?: "inline" | "bar";
  },
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState(DISMISS_REASONS[0]!.reason);
  const [sheet, setSheet] = useState(false);
  const status = typeof finding.status === "string" ? finding.status : "";
  const closed = status === "fixed" || status === "dismissed";
  const reasonId = `bob-dismiss-reason-${finding.id}`;

  // The busy check is the guard against a double click.
  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        await action();
        setSheet(false);
        await onChanged();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [busy, onChanged],
  );

  const reasonPicker = (
    <>
      <label htmlFor={reasonId} className="bob-sr">Dismiss reason</label>
      <select id={reasonId} className="bob-select" value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}>
        {DISMISS_REASONS.map((r) => (
          <option key={r.reason} value={r.reason} title={r.hint}>{r.reason}</option>
        ))}
      </select>
    </>
  );
  const dismiss = () => void run(() => dismissFinding(project, finding.id, reason));
  const reopen = (
    <button
      type="button"
      className="bob-btn bob-reopen-btn"
      disabled={busy || !closed}
      title={closed
        ? "Puts this finding back in front of Bob's next pass and clears what closed it. Use it when a dismissal was wrong, or when a defect Bob recorded as gone is not."
        : "Only a fixed or dismissed finding can be reopened."}
      onClick={() => void run(() => reopenFinding(project, finding.id))}
    >
      Reopen
    </button>
  );

  if (layout === "bar") {
    return (
      <div className="bob-admin-actions" data-admin-actions="">
        {!closed && (
          <button type="button" className="bob-btn bob-dismiss-btn" aria-expanded={sheet} disabled={busy} onClick={() => setSheet(!sheet)}>
            Dismiss
          </button>
        )}
        {reopen}
        {(sheet || error) && (
          <div className="bob-bar-sheet">
            {sheet && !closed && (
              <div role="group" aria-label="Dismiss this finding" className="bob-sheet-body">
                {reasonPicker}
                <p className="bob-action-note">{DISMISS_NOTE}</p>
                <div className="bob-sheet-buttons">
                  <button type="button" className="bob-btn" onClick={() => setSheet(false)}>Cancel</button>
                  <button type="button" className="bob-btn bob-btn-danger" disabled={busy} onClick={dismiss}>Dismiss it</button>
                </div>
              </div>
            )}
            {error && <div className="bob-error-line" role="alert">{error}</div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bob-admin-actions" data-admin-actions="">
      {!closed && (
        <span className="bob-dismiss">
          {reasonPicker}
          <button
            type="button"
            className="bob-btn bob-dismiss-btn"
            disabled={busy}
            title={`Dismisses this finding, recording the reason ${reason}. Permanent: Bob absorbs later sightings of the same defect through its fingerprint, so he will not raise it again.`}
            onClick={dismiss}
          >
            Dismiss
          </button>
        </span>
      )}
      {reopen}
      <p className="bob-action-note">{actionNote(status)}</p>
      {error && <div className="bob-error-line" role="alert">{error}</div>}
    </div>
  );
}
